import clsx from "clsx";
import type { FunctionComponent } from "react";

import Card from "~/components/Card/Card";
import DetailsListDivider from "~/components/DetailsList/components/DetailsListDivider";
import DetailsListItem from "~/components/DetailsList/components/DetailsListItem";
import Hint from "~/components/Hint";
import { getFormattedCurrencyValue } from "~/modules/billing/api/invoiceStatus/invoiceStatusId.ts";
import type { InvoiceAction } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import { formatNumberWithComma } from "~/utils/numberUtils.ts";

type InvoiceAmountDetailProps = {
	bold?: boolean;
	currencySymbol: string;
	label: string;
	valueCents: number;
}

const InvoiceAmountDetail: FunctionComponent<InvoiceAmountDetailProps> = ({
	currencySymbol,
	bold = false,
	label,
	valueCents,
}) => {
	return <DetailsListItem label={label}
							boldLabel={bold}>
		<div className={clsx("w-full text-right", bold && "font-bold", valueCents < 0 && "text-secondary-500")}>{getFormattedCurrencyValue(valueCents, currencySymbol)}</div>
	</DetailsListItem>;
};

type Props = {
	currencySymbol: string;
	editTotalsAction: InvoiceAction;
	onEditClick?: () => void;
	showTravelCosts?: boolean;
	subtotalCents: number;
	totalAmountCents: number;
	// Only for a project invoice. The card shows it as information only.
	totalTrackedCents?: number;
	travelCostCentsBeforeVat: number;
	vatPercentage: number;
	vatTotalCents: number;
};

const InvoiceTotalsSection: FunctionComponent<Props> = ({
	currencySymbol,
	editTotalsAction,
	onEditClick,
	showTravelCosts = true,
	subtotalCents,
	totalAmountCents,
	totalTrackedCents,
	travelCostCentsBeforeVat,
	vatPercentage,
	vatTotalCents,
}) => {
	const showsTrackedTotal = totalTrackedCents !== undefined;
	const trackedTotalExceedsSubtotal = showsTrackedTotal && totalTrackedCents > subtotalCents;

	return <Card title="Rechnungsbetrag"
				 onHeaderButtonClick={onEditClick}
				 headerButtonText={onEditClick ? "Bearbeiten" : undefined}
				 headerButtonDisabled={!editTotalsAction.allowed}
				 headerButtonDisabledReason={editTotalsAction.reason}
				 padding="none">
		<div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 px-4 py-3">
			{showsTrackedTotal && <>
				<dt className="text-sm italic leading-6 text-gray-500">Summe Erfassungen</dt>
				<dd className={clsx("text-right text-sm italic leading-6",
					trackedTotalExceedsSubtotal ? "font-bold text-red-600" : "text-gray-500")}>
					{getFormattedCurrencyValue(totalTrackedCents, currencySymbol)}
				</dd>
				{trackedTotalExceedsSubtotal && <div className="col-span-2">
					<Hint size="sm"
						  theme="error">
						Die Summe der Erfassungen ist höher als die Summe der Einzelpositionen. Bitte prüfe die
						Einzelpositionen und passe den Rechnungsbetrag bei Bedarf an.
					</Hint>
				</div>}
				<DetailsListDivider />
			</>}
			<InvoiceAmountDetail label="Summe Einzelpositionen"
								 valueCents={subtotalCents}
								 currencySymbol={currencySymbol} />
			{showTravelCosts && <InvoiceAmountDetail label="Reisekosten"
													 valueCents={travelCostCentsBeforeVat}
													 currencySymbol={currencySymbol} />}
			<InvoiceAmountDetail label="Rechnungssumme"
								 valueCents={travelCostCentsBeforeVat + subtotalCents}
								 currencySymbol={currencySymbol} />
			<InvoiceAmountDetail label={`MwSt ${formatNumberWithComma(vatPercentage / 100, 2, true)}%`}
								 valueCents={vatTotalCents}
								 currencySymbol={currencySymbol} />
			<DetailsListDivider bold={true} />
			<InvoiceAmountDetail label="Gesamt"
								 valueCents={totalAmountCents}
								 currencySymbol={currencySymbol}
								 bold={true} />
		</div>
	</Card>;
};

export default InvoiceTotalsSection;